import {
  ASSET_TYPE_LIST,
  CYBER_PAYLOAD_ESCALATION_CAUSES,
  DELTAV_COSTS,
  GROUND_SDA_BAND_MODIFIERS,
  ISR_SENSOR_CAPABILITY,
  ISR_SENSOR_MISSION_MODIFIERS,
  MODIFIERS_BY_ID,
  MULTIPLIER_PIPELINE,
  RESTRICTIVE_WEATHER_MODIFIER,
  ROE_VIOLATION_VP_PENALTY,
  SDA_SAT_ONE_BAND_AWAY_MODIFIER,
  SDA_SAT_SAME_BAND_MODIFIER,
  SENSOR_WEATHER_AFFECTED,
  bandSeparation,
  escalationPreviewFor,
  missionDef,
  type Band,
  type CyberPayload,
  type MissionId,
  type SensorClass,
} from '@badger-sentinel/rules';
import type { CellViewWire, OrderDraft, OrderTargetWire } from '@badger-sentinel/protocol';

import {
  assetOf,
  bandOfOrder,
  deltaVCostFor,
  regionOfOrder,
  targetKindOf,
  type BuilderDraft,
  type ParamValue,
} from './builder.js';
import { floorOf } from './escalation.js';
import {
  missionLabel,
  paramSpecOf,
  targetKindLabel,
  targetKindPhrase,
  tnBasisOf,
  type TnBasis,
} from './missions.js';
import type { FriendlyAssetViewWire } from './view-types.js';

/**
 * The success ladder and the block reading (Q7, design 1545–1600 and 372–420).
 *
 * ## Never compute rules — read the tables
 *
 * Every number here comes out of `packages/rules`: `MODIFIERS_BY_ID`, the sensor
 * matrices, `RESTRICTIVE_WEATHER_MODIFIER`, `DELTAV_COSTS`,
 * `ROE_VIOLATION_VP_PENALTY`, `MULTIPLIER_PIPELINE`. Nothing is a second
 * implementation of the resolver, and the server revalidates all of it (§7.3).
 * What the client adds is *arithmetic on what it was told*.
 *
 * ## The `±?` row is the point of the ladder
 *
 * It is present unconditionally, on every mission, whatever else the ladder
 * knows. It is not a fallback for a client that could not compute: it is the
 * statement that unknown factors exist. Almost every modifier in
 * `MODIFIERS_BY_ID` reads a field the projection deliberately withholds — a
 * contact's signature, whether it is emitting, whether it manoeuvred, whether it
 * is under EMCON — so a ladder that stopped at what the seat can see would read
 * as a complete accounting, which is a lie the server can see and the player
 * cannot.
 */

export type LadderTone = 'good' | 'bad' | 'warn' | 'neutral';

export interface LadderRow {
  readonly key: string;
  readonly label: string;
  readonly detail: string;
  /** The right column: a signed percentage, a word (`NO GO`), or `±?`. */
  readonly mod: string;
  readonly tone: LadderTone;
}

/** A reason this order cannot be queued, in the server's own vocabulary. */
export interface OrderBlock {
  readonly reason: string;
  readonly detail: string;
}

/** The ROE bill on an order the server will nonetheless **accept** (M3-D13). */
export interface RoePrice {
  readonly vp: number;
  readonly escalationFactor: number;
  readonly note: string;
  /** A scenario that wanted a wall rather than a bill (`hardBlock`). */
  readonly blocks: boolean;
}

export interface SuccessLadder {
  readonly basis: TnBasis;
  /** The estimate line's figure: `55%`, `AUTOMATIC`, or `—`. */
  readonly headline: string;
  readonly headlineTone: LadderTone;
  /** The loudest modifier, for the resident one-line summary. */
  readonly top: string;
  readonly rows: readonly LadderRow[];
  readonly block: OrderBlock | null;
  readonly roe: RoePrice | null;
  /** `+6–12`, `≥ +1`, `+0–2` — never one number (Phase 0.5 finding 7). */
  readonly escalation: string | null;
  readonly footnote: string;
}

/** The `±?` row. One definition, so nothing can render a ladder without it. */
export const UNDISCLOSED_ROW: LadderRow = Object.freeze({
  key: 'UNDISCLOSED',
  label: 'UNDISCLOSED',
  detail: 'FACTORS NOT HELD BY THIS SEAT',
  mod: '±?',
  tone: 'neutral' as LadderTone,
});

/** A d20 plus modifiers against the target number, so a step is worth 5 points. */
const SUCCESS_FLOOR_PCT = 5;
const SUCCESS_CEILING_PCT = 95;

/**
 * Chance that `d20 + modifiers >= tn`.
 *
 * Clamped at 5 and 95 because `classify` makes a natural 20 always succeed and a
 * natural 1 always fail, whatever the arithmetic says (RB §1.4).
 */
export function successPct(tn: number, modifierTotal: number): number {
  const effective = tn - modifierTotal;
  const raw = ((21 - effective) / 20) * 100;
  return Math.max(SUCCESS_FLOOR_PCT, Math.min(SUCCESS_CEILING_PCT, Math.round(raw)));
}

function signedPct(points: number): string {
  if (points === 0) return '±0%';
  return (points > 0 ? '+' : '−') + String(Math.abs(points)) + '%';
}

/** A target-number step is five points of chance. One unit throughout the ladder. */
function asPct(modifier: number): number {
  return modifier * 5;
}

const SENSOR_CLASSES: ReadonlyMap<string, SensorClass> = new Map(
  ASSET_TYPE_LIST.map((type) => [type.id, type.sensorClass]),
);

/**
 * The acting asset's sensor class, or `null` for a type this build has never
 * heard of.
 *
 * A scenario may declare its own asset types (§10.4.2) and they are not in the
 * bundled table. `null` therefore means *unknown*, and every row that depends on
 * it is **absent** rather than computed from a default — M5-D10, in the one
 * place in this phase where a plausible default was available.
 */
export function sensorClassOf(asset: FriendlyAssetViewWire): SensorClass | null {
  return SENSOR_CLASSES.get(asset.typeId) ?? null;
}

export interface LadderInput {
  readonly view: CellViewWire;
  readonly draft: BuilderDraft;
  /** Orders already queued this turn, for the capacity reading. */
  readonly queuedMissionsByAsset?: Readonly<Record<string, number>>;
}

/**
 * Everything the estimate line, the overlay and the queue card's popover read.
 *
 * One reading, shared by the draft and by an order already in the queue: the
 * seat should never have to reconstruct why a number is what it is from memory
 * of the moment they committed it.
 */
export function successLadder(input: LadderInput): SuccessLadder | null {
  const { view, draft } = input;
  const mission = draft.mission;
  if (mission === null) return null;
  const asset = assetOf(view, draft.asset);
  const basis = tnBasisOf(mission);

  const rows: LadderRow[] = [];
  const modifiers: { readonly label: string; readonly value: number }[] = [];

  rows.push(baseRow(mission, basis));

  if (asset !== null) {
    for (const row of actorRows(view, asset, mission, draft.target, modifiers)) rows.push(row);
  }
  for (const row of targetRows(view, mission, draft.target, modifiers)) rows.push(row);
  for (const row of environmentRows(view, asset, mission, draft.target, modifiers)) rows.push(row);

  const roe = roePriceFor(view, mission, draft);
  if (roe !== null) {
    rows.push({
      key: 'ROE',
      label: 'ROE',
      detail:
        roe.note +
        ' · ESCALATION ×' +
        String(roe.escalationFactor) +
        (roe.blocks ? ' · BLOCKED' : ''),
      mod: '−' + String(roe.vp) + ' VP',
      tone: 'bad',
    });
  }

  const total = modifiers.reduce((sum, modifier) => sum + modifier.value, 0);
  const headline = headlineFor(basis, total, rows);

  if (basis.kind === 'FLAT') {
    const uncapped = ((21 - (basis.tn - total)) / 20) * 100;
    const capped = successPct(basis.tn, total);
    if (Math.round(uncapped) !== capped) {
      rows.push({
        key: 'LIMIT',
        label: 'LIMIT',
        detail:
          'CHANCE CAPPED AT ' + String(SUCCESS_FLOOR_PCT) + '–' + String(SUCCESS_CEILING_PCT) + '%',
        mod: signedPct(capped - Math.round(uncapped)),
        tone: 'neutral',
      });
    }
  }

  rows.push(UNDISCLOSED_ROW);

  const loudest = modifiers.reduce<{ label: string; value: number } | null>(
    (best, modifier) =>
      modifier.value !== 0 && (best === null || Math.abs(modifier.value) > Math.abs(best.value))
        ? modifier
        : best,
    null,
  );

  return {
    basis,
    headline: headline.text,
    headlineTone: headline.tone,
    top:
      loudest === null
        ? 'NO MODIFIERS IN PLAY'
        : loudest.label + ' ' + signedPct(asPct(loudest.value)),
    rows,
    block: blockFor(input),
    roe,
    escalation: escalationRangeFor(mission, draft.params),
    footnote: 'ESTIMATE · UNDISCLOSED FACTORS MAY APPLY · RESOLVED AT TURN END',
  };
}

// ---------------------------------------------------------------------------
// The base row — where `tn: null`'s two meanings are drawn apart
// ---------------------------------------------------------------------------

function baseRow(mission: MissionId, basis: TnBasis): LadderRow {
  switch (basis.kind) {
    case 'FLAT':
      return {
        key: 'BASE',
        label: 'BASE',
        detail: missionLabel(mission) + ' · TN ' + String(basis.tn),
        mod: String(successPct(basis.tn, 0)) + '%',
        tone: 'neutral',
      };
    case 'DETERMINISTIC':
      // *Not* 100%: no roll is made at all, and a chance of 100 % invites the
      // reading that a very good roll could still do better.
      return {
        key: 'BASE',
        label: 'BASE',
        detail: missionLabel(mission) + ' · NO ROLL',
        mod: 'AUTOMATIC',
        tone: 'good',
      };
    case 'VARIABLE':
      return {
        key: 'BASE',
        label: 'BASE',
        detail: basis.detail,
        mod: 'TN WITHHELD',
        tone: 'warn',
      };
  }
}

function headlineFor(
  basis: TnBasis,
  total: number,
  rows: readonly LadderRow[],
): { readonly text: string; readonly tone: LadderTone } {
  void rows;
  switch (basis.kind) {
    case 'FLAT': {
      const pct = successPct(basis.tn, total);
      return { text: String(pct) + '%', tone: pct >= 65 ? 'good' : pct >= 40 ? 'neutral' : 'bad' };
    }
    case 'DETERMINISTIC':
      return { text: 'AUTOMATIC', tone: 'good' };
    case 'VARIABLE':
      return { text: '?', tone: 'warn' };
  }
}

// ---------------------------------------------------------------------------
// Rows, each from a real table
// ---------------------------------------------------------------------------

function actorRows(
  view: CellViewWire,
  asset: FriendlyAssetViewWire,
  mission: MissionId,
  target: OrderTargetWire | null,
  modifiers: { label: string; value: number }[],
): readonly LadderRow[] {
  const rows: LadderRow[] = [];

  const degraded = MODIFIERS_BY_ID.ACTOR_DEGRADED;
  if (asset.status === 'DEGRADED' && degraded !== undefined) {
    modifiers.push({ label: 'DEGRADED', value: degraded.value });
    rows.push({
      key: 'ACTOR_DEGRADED',
      label: 'ASSET STATE',
      detail: degraded.label.toUpperCase(),
      mod: signedPct(asPct(degraded.value)),
      tone: 'bad',
    });
  }

  const sensorClass = sensorClassOf(asset);
  const band = bandOfOrder(view, asset, target);
  if (sensorClass !== null && band !== null) {
    const groundRow = groundSdaModifier(sensorClass, band);
    if (groundRow !== null) {
      modifiers.push({ label: 'SENSOR', value: groundRow });
      rows.push({
        key: 'SENSOR_BAND',
        label: 'SENSOR',
        detail: sensorClass + ' · ' + band,
        mod: signedPct(asPct(groundRow)),
        tone: groundRow < 0 ? 'bad' : groundRow > 0 ? 'good' : 'neutral',
      });
    } else if (sensorClass === 'SDA_SAT' && asset.location.kind === 'ORBIT') {
      const separation = bandSeparation(asset.location.band, band);
      const value =
        separation === 0 ? SDA_SAT_SAME_BAND_MODIFIER : SDA_SAT_ONE_BAND_AWAY_MODIFIER[band];
      if (separation <= 1) {
        modifiers.push({ label: 'SENSOR', value });
        rows.push({
          key: 'SENSOR_BAND',
          label: 'SENSOR',
          detail: 'SDA SATELLITE · ' + (separation === 0 ? 'SAME BAND' : 'ONE BAND AWAY'),
          mod: signedPct(asPct(value)),
          tone: value < 0 ? 'bad' : value > 0 ? 'good' : 'neutral',
        });
      }
    }
  }

  if (sensorClass !== null) {
    const isr = ISR_SENSOR_MISSION_MODIFIERS[sensorClass][mission];
    if (isr !== undefined) {
      modifiers.push({ label: 'COLLECTOR', value: isr });
      rows.push({
        key: 'ISR_SENSOR',
        label: 'COLLECTOR',
        detail: sensorClass + ' · ' + missionLabel(mission),
        mod: signedPct(asPct(isr)),
        tone: isr < 0 ? 'bad' : 'good',
      });
    }
  }

  const cost = deltaVCostFor(view, asset, mission, target);
  if (cost !== null && cost > 0) {
    const remaining = asset.deltaV === null ? null : asset.deltaV - cost;
    rows.push({
      key: 'DELTAV',
      label: 'DELTA-V',
      detail:
        remaining === null
          ? 'BUDGET NOT HELD FOR THIS ASSET'
          : String(remaining.toFixed(0)) +
            ' M/S LEFT · ' +
            String(DELTAV_COSTS.STATION_KEEPING) +
            ' OWED',
      mod: String(cost) + ' M/S',
      tone: remaining !== null && remaining < DELTAV_COSTS.STATION_KEEPING ? 'bad' : 'neutral',
    });
  }

  return rows;
}

function groundSdaModifier(sensorClass: SensorClass, band: Band): number | null {
  if (sensorClass === 'GROUND_RADAR' || sensorClass === 'GROUND_OPTICAL') {
    return GROUND_SDA_BAND_MODIFIERS[sensorClass][band];
  }
  return null;
}

function targetRows(
  view: CellViewWire,
  mission: MissionId,
  target: OrderTargetWire | null,
  modifiers: { label: string; value: number }[],
): readonly LadderRow[] {
  const def = missionDef(mission);
  const rows: LadderRow[] = [];
  if (def === undefined) return rows;

  const contact =
    target !== null && target.kind === 'CONTACT'
      ? (view.contacts.find((candidate) => candidate.id === target.contact) ?? null)
      : null;

  if (def.requiresRung !== null) {
    rows.push({
      key: 'RUNG',
      label: 'TRACK',
      detail:
        contact === null
          ? 'REQUIRES ' + def.requiresRung
          : contact.rung + ' · REQUIRES ' + def.requiresRung,
      mod: contact === null ? '—' : contact.rung,
      tone: contact !== null && rungMeets(contact.rung, def.requiresRung) ? 'good' : 'bad',
    });
  }

  // The warning that made this order legal at all (RB §5.7.5, M4-D7). Two rows,
  // and the second one is the point: the release carries `stageModifier` and
  // never the total, because the total is computed against the launch's *true*
  // model — so a seat shown `+4` alone would read it as the whole modifier and
  // get a free check on another cell's work. The missing term is named.
  if (target !== null && target.kind === 'MW_EVENT') {
    const release = view.mwReleases.find((candidate) => candidate.eventId === target.event);
    if (release !== undefined) {
      rows.push({
        key: 'MW_RELEASE',
        label: 'WARNING',
        detail:
          release.assignedIdentity + ' · RELEASED AT STAGE ' + String(release.releasedAtStage),
        mod: (release.stageModifier >= 0 ? '+' : '') + String(release.stageModifier),
        tone: release.stageModifier > 0 ? 'good' : 'neutral',
      });
      rows.push({
        key: 'MW_CLASSIFICATION',
        label: 'CLASSIFICATION',
        detail: 'GRADED AGAINST THE LAUNCH · NOT HELD',
        mod: '?',
        tone: 'warn',
      });
    }
  }

  const characterized = MODIFIERS_BY_ID.TARGET_CHARACTERIZED;
  if (contact !== null && contact.characterized && characterized !== undefined) {
    modifiers.push({ label: 'CHARACTERIZED', value: characterized.value });
    rows.push({
      key: 'CHARACTERIZED',
      label: 'TARGET',
      detail: characterized.label.toUpperCase(),
      mod: signedPct(asPct(characterized.value)),
      tone: 'good',
    });
  }

  // `requiresEmittingTarget` is a legality gate on a field the projection does
  // not carry — `ContactView` has no emission state, deliberately. So it is
  // stated as the requirement it is and **not** drawn as a block: refusing an
  // order the seat may well be allowed to give is worse than letting the server
  // answer, and pretending the requirement does not exist is worse than both.
  if (def.requiresEmittingTarget) {
    rows.push({
      key: 'EMITTING',
      label: 'EMISSION',
      detail: 'TARGET MUST BE EMITTING THIS TURN',
      mod: 'NOT HELD',
      tone: 'warn',
    });
  }

  const cyber = view.cyber.contacts.find(
    (candidate) =>
      target !== null && target.kind === 'CONTACT' && candidate.alias === target.contact,
  );
  if (cyber !== undefined) {
    rows.push({
      key: 'HARDENING',
      label: 'HARDENING',
      // `null` here is *unknown until Reconnoiter succeeds* (`data/cyber.ts`), and
      // it is drawn as unknown rather than as NORMAL.
      detail: cyber.network + ' · ' + (cyber.hardening ?? 'NOT REVEALED'),
      mod: cyber.hardening ?? '?',
      tone: cyber.hardening === null ? 'warn' : 'neutral',
    });
    rows.push({
      key: 'ACCESS',
      label: 'ACCESS',
      detail: cyber.persistence ? cyber.access + ' · PERSISTENT' : cyber.access,
      mod: cyber.access,
      tone: cyber.access === 'NO_ACCESS' ? 'warn' : 'good',
    });
  }

  return rows;
}

function environmentRows(
  view: CellViewWire,
  asset: FriendlyAssetViewWire | null,
  mission: MissionId,
  target: OrderTargetWire | null,
  modifiers: { label: string; value: number }[],
): readonly LadderRow[] {
  const rows: LadderRow[] = [];

  const region = regionOfOrder(view, target);
  const weather = region === null ? null : (view.theatre.weather[region] ?? null);
  if (region !== null && weather !== null) {
    const sensorClass = asset === null ? null : sensorClassOf(asset);
    const affected = sensorClass === null ? null : SENSOR_WEATHER_AFFECTED[sensorClass];
    const value = weather === 'RESTRICTIVE' ? RESTRICTIVE_WEATHER_MODIFIER : 0;
    if (affected === true && value !== 0) modifiers.push({ label: 'WEATHER', value });
    rows.push({
      key: 'WEATHER',
      label: 'WEATHER',
      detail: region + ' · ' + weather,
      mod:
        affected === null
          ? weather
          : affected
            ? weather === 'PROHIBITIVE'
              ? 'NO GO'
              : signedPct(asPct(value))
            : 'ALL-WEATHER',
      tone: affected === true && weather !== 'CLEAR' ? 'bad' : 'neutral',
    });
  }

  const band = bandOfOrder(view, asset, target);
  if (band !== null) {
    const level = view.theatre.debris[band];
    rows.push({
      key: 'DEBRIS',
      label: 'DEBRIS',
      detail: band + ' · LEVEL ' + String(level),
      // Debris does not move *this* roll — RB §8.2 prices it on the conjunction
      // avoidance roll in phase 8. Saying `±0%` would be a claim that it is
      // harmless; naming the roll it does move is the true statement.
      mod: level === 0 ? 'CLEAR' : 'CONJUNCTION',
      tone: level >= 4 ? 'bad' : level > 0 ? 'warn' : 'neutral',
    });
  }

  void mission;
  return rows;
}

function rungMeets(rung: string, required: string): boolean {
  const ladder = ['HIDDEN', 'FOUND', 'FIXED', 'TRACKED'];
  return ladder.indexOf(rung) >= ladder.indexOf(required);
}

// ---------------------------------------------------------------------------
// ROE — priced, not forbidden (M3-D13, RB §7.3)
// ---------------------------------------------------------------------------

/**
 * The bill on an order the server will **accept**.
 *
 * `violatedNow` is computed server-side and shipped on `view.roe[]`, so the
 * client reads the verdict rather than recomputing `roeViolatedAt`. A rule
 * scoped to a `weaponType` cannot be matched here at all — the projection does
 * not carry a launcher's round (Phase 0.5 finding 7) — so such a rule is left to
 * the server, and its warning arrives on the accepted order.
 */
export function roePriceFor(
  view: CellViewWire,
  mission: MissionId,
  draft: BuilderDraft,
): RoePrice | null {
  const factor =
    MULTIPLIER_PIPELINE.find((multiplier) => multiplier.source === 'ROE_VIOLATION')?.factor ?? 1;
  for (const rule of view.roe) {
    if (rule.mission !== mission) continue;
    if (!rule.violatedNow) continue;
    if (rule.weaponType !== null) continue;
    if (rule.payload !== null && rule.payload !== draft.params.payload) continue;
    return {
      vp: ROE_VIOLATION_VP_PENALTY,
      escalationFactor: factor,
      note: rule.note ?? 'OUTSIDE THIS CELL’S AUTHORISATION',
      blocks: rule.hardBlock,
    };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Escalation preview — a range, never a figure
// ---------------------------------------------------------------------------

/**
 * What one order may add to the counter.
 *
 * The same reading `model/escalation.ts` does for the whole queue, at n = 1, and
 * through the same `floorOf` — two functions computing a floor two ways is how
 * the status row and the card come to disagree about one order.
 *
 * **A chosen parameter narrows it.** `DEPLOY_PAYLOAD` lists five rows because the
 * payload decides which one lands; once the seat has picked one there is nothing
 * left to be uncertain about except whether it lands at all, so the preview drops
 * to that row. The seat knows what it chose, and a range that ignores their own
 * choice is not honesty, it is arithmetic nobody updated.
 */
export function escalationRangeFor(
  mission: MissionId,
  params: Readonly<Record<string, ParamValue>> = {},
): string | null {
  const all = escalationPreviewFor(mission);
  if (all.length === 0) return null;
  const chosen = params.payload;
  const narrowed =
    typeof chosen === 'string' &&
    CYBER_PAYLOAD_ESCALATION_CAUSES[chosen as CyberPayload] !== undefined
      ? all.filter((cause) => cause.id === CYBER_PAYLOAD_ESCALATION_CAUSES[chosen as CyberPayload])
      : all;
  const causes = narrowed.length === 0 ? all : narrowed;

  const low = floorOf(causes);
  const high = Math.max(...causes.map((cause) => cause.value));
  if (high === 0) return '+0';
  return low === high ? '≥ +' + String(low) : '+' + String(low) + '–' + String(high);
}

// ---------------------------------------------------------------------------
// Blocks
// ---------------------------------------------------------------------------

/**
 * Why this order cannot be queued, or `null`.
 *
 * Ordered the way `validate.ts` orders its checks: most specific and most
 * actionable first, because the order decides only the quality of the message.
 * **Every reason is one of `ORDER_INVALID_REASONS`** — the same vocabulary that
 * will come back on the wire if the seat sends it anyway — so a block the client
 * showed and a rejection the server sent read as the same condition.
 *
 * Checks that need a field the projection withholds are **absent**, not guessed:
 * a launcher's weapon range needs `weaponType`, and a target's emission state is
 * not on `ContactView`.
 */
export function blockFor(input: LadderInput): OrderBlock | null {
  const { view, draft } = input;
  const mission = draft.mission;
  if (mission === null || draft.asset === null) return null;
  const asset = assetOf(view, draft.asset);
  if (asset === null) {
    return { reason: 'ASSET_NOT_FOUND', detail: draft.asset + ' IS NOT IN THIS PICTURE' };
  }
  if (!asset.orderable || asset.assignedCell !== view.viewer.cell) {
    return {
      reason: 'ASSET_WRONG_CELL',
      detail: asset.id + ' IS ASSIGNED TO ' + asset.assignedCell,
    };
  }

  const def = missionDef(mission);
  if (def === undefined) {
    return {
      reason: 'MISSION_NOT_IMPLEMENTED',
      detail: mission + ' IS NOT RESOLVED BY THIS BUILD',
    };
  }
  if (!asset.missions.includes(mission)) {
    return {
      reason: 'MISSION_NOT_LEGAL_FOR_TYPE',
      detail: asset.typeName.toUpperCase() + ' CANNOT PERFORM ' + missionLabel(mission),
    };
  }

  if (asset.status === 'OFFLINE') {
    return { reason: 'ASSET_OFFLINE', detail: asset.id + ' IS OFFLINE · NO MISSIONS OF ANY KIND' };
  }
  if (asset.status === 'DEGRADED' && def.forbiddenWhenDegraded) {
    return {
      reason: 'FORBIDDEN_WHILE_DEGRADED',
      detail: 'A DEGRADED ASSET CANNOT PERFORM ' + missionLabel(mission),
    };
  }
  if (!asset.controllable) {
    return {
      reason: 'ASSET_UNCONTROLLABLE',
      detail: asset.id + ' CANNOT BE COMMANDED · ' + (asset.controlLoss ?? 'CONTROL LOST'),
    };
  }
  if (asset.location.kind === 'TRANSIT') {
    return { reason: 'ASSET_IN_TRANSIT', detail: asset.id + ' IS IN TRANSIT' };
  }
  if (view.cyber.friendlyIsolated.includes(asset.id)) {
    return {
      reason: 'ASSET_ISOLATED',
      detail: asset.id + ' IS CYBER-ISOLATED AND TAKES NO ORDERS',
    };
  }

  const queued = input.queuedMissionsByAsset?.[asset.id] ?? 0;
  if (asset.missionsThisTurn.length + queued >= asset.capacity) {
    return {
      reason: 'CAPACITY_EXCEEDED',
      detail: asset.id + ' HAS CAPACITY ' + String(asset.capacity),
    };
  }

  const target = draft.target ?? { kind: 'NONE' as const };
  // `targetKindOf`, not `target.kind`: `MissionDef.targetKinds` speaks the
  // **domain's** vocabulary, which has no `OWN_ASSET` and does not need one — the
  // server turns that arm into a `CONTACT` carrying a true `AssetId` (Q24). Read
  // raw, every friendly-target mission is blocked against a table that is
  // correct, and the seat gets a picker that offers `HARDEN`, offers its own
  // TT&C station, and then leaves ADD TO QUEUE dead with no reason on screen.
  if (!(def.targetKinds as readonly string[]).includes(targetKindOf(target))) {
    return {
      reason: 'TARGET_WRONG_KIND',
      detail:
        target.kind === 'NONE'
          ? missionLabel(mission) + ' NEEDS ' + def.targetKinds.map(targetKindPhrase).join(' OR ')
          : missionLabel(mission) + ' TARGETS ' + def.targetKinds.map(targetKindLabel).join(' / '),
    };
  }

  if (target.kind === 'CONTACT' && def.requiresRung !== null) {
    const contact = view.contacts.find((candidate) => candidate.id === target.contact);
    if (contact === undefined) {
      return { reason: 'TARGET_NOT_FOUND', detail: target.contact + ' IS NOT A KNOWN CONTACT' };
    }
    if (!rungMeets(contact.rung, def.requiresRung)) {
      return {
        reason: 'TARGET_RUNG_TOO_LOW',
        detail:
          missionLabel(mission) + ' REQUIRES ' + def.requiresRung + ' · TRACK IS ' + contact.rung,
      };
    }
  }

  const rangeBlock = rangeBlockFor(view, asset, target);
  if (rangeBlock !== null) return rangeBlock;

  const sensorClass = sensorClassOf(asset);
  if (sensorClass !== null) {
    const capability = ISR_SENSOR_CAPABILITY[sensorClass];
    const isIsr = Object.values(ISR_SENSOR_CAPABILITY).some((list) => list.includes(mission));
    if (isIsr && !capability.includes(mission)) {
      return {
        reason: 'SENSOR_CANNOT_PERFORM',
        detail: 'A ' + sensorClass + ' SENSOR CANNOT PERFORM ' + missionLabel(mission),
      };
    }
    const region = regionOfOrder(view, target);
    if (
      SENSOR_WEATHER_AFFECTED[sensorClass] &&
      region !== null &&
      view.theatre.weather[region] === 'PROHIBITIVE'
    ) {
      return {
        reason: 'WEATHER_PROHIBITIVE',
        detail: 'PROHIBITIVE WEATHER OVER ' + region + ' BLOCKS OPTICAL COLLECTION',
      };
    }
  }

  const cost = deltaVCostFor(view, asset, mission, draft.target);
  if (cost === null) {
    return { reason: 'BAD_PARAMETERS', detail: missionLabel(mission) + ' CANNOT REACH THAT SLOT' };
  }
  if (cost > 0 && asset.deltaV !== null) {
    const available = asset.deltaV - DELTAV_COSTS.STATION_KEEPING;
    if (available < cost) {
      return {
        reason: 'INSUFFICIENT_DELTAV',
        detail:
          missionLabel(mission) +
          ' COSTS ' +
          String(cost) +
          ' M/S · ' +
          String(asset.deltaV) +
          ' HELD, ' +
          String(DELTAV_COSTS.STATION_KEEPING) +
          ' OWED',
      };
    }
  }

  if (asset.magazine === 0 && (mission === 'LAUNCH' || mission === 'INTERCEPT')) {
    return { reason: 'MAGAZINE_EMPTY', detail: asset.id + ' HAS NO ROUNDS LEFT' };
  }

  const spec = paramSpecOf(mission);
  if (spec !== null && spec.required && draft.params[spec.key] === undefined) {
    return {
      reason: 'BAD_PARAMETERS',
      detail: missionLabel(mission) + ' NEEDS A ' + spec.label,
    };
  }

  const roe = roePriceFor(view, mission, draft);
  if (roe !== null && roe.blocks) {
    return { reason: 'ROE_FORBIDDEN', detail: roe.note.toUpperCase() };
  }

  return null;
}

/**
 * The one range rule the client can read.
 *
 * RB §5.2.1's "SDA satellites cannot act two or more bands away" is checked at
 * *submission* (`validate.ts`) and needs only the sensor class and two bands. A
 * launcher's declared range is **not** here: `phase7-kinetic` checks it at
 * resolution against the round fired, and `FriendlyAssetView` carries no
 * `weaponType` — so the client would have to guess which weapon, which is the
 * one thing M5-D10 forbids.
 */
function rangeBlockFor(
  view: CellViewWire,
  asset: FriendlyAssetViewWire,
  target: OrderTargetWire,
): OrderBlock | null {
  if (sensorClassOf(asset) !== 'SDA_SAT') return null;
  if (asset.location.kind !== 'ORBIT') return null;
  const band = bandOfOrder(view, asset, target);
  if (band === null || target.kind === 'SELF') return null;
  const separation = bandSeparation(asset.location.band, band);
  if (separation < 2) return null;
  return {
    reason: 'TARGET_OUT_OF_RANGE',
    detail:
      'SDA SATELLITES CANNOT ACT TWO OR MORE BANDS AWAY · ' + asset.location.band + ' → ' + band,
  };
}

/** The ladder for an order already in the queue. */
export function ladderForOrder(
  view: CellViewWire,
  order: OrderDraft,
  queuedMissionsByAsset?: Readonly<Record<string, number>>,
): SuccessLadder | null {
  return successLadder({
    view,
    draft: {
      asset: order.asset,
      mission: order.mission,
      target: order.target,
      params: order.params ?? {},
    },
    ...(queuedMissionsByAsset === undefined ? {} : { queuedMissionsByAsset }),
  });
}
